import { useEffect, useRef, useState } from "react";
import type { CSSProperties, DragEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { adminApi, type Company } from "../../api/admin";
import { importsApi } from "../../api/imports";
import type { ApiError } from "../../api/client";
import Ic from "../../components/icons.jsx";
import { CompanyCombobox } from "../../components/CompanyCombobox";
import { ImportStepper } from "./ImportStepper";

interface Props {
  onClose: () => void;
}

export function ImportOrderModal({ onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void adminApi
      .listCompanies(0, 200)
      .then((p) => setCompanies(p.content.filter(Boolean)))
      .catch(() => undefined);
  }, []);

  async function handleFile(file: File) {
    if (!companyId || busy) return;
    setError(null);
    setBusy(true);
    try {
      const result = await importsApi.upload(companyId, file);
      navigate(`/orders/imports/${result.id}`);
    } catch (err) {
      setError((err as ApiError).message ?? "Upload failed");
      setBusy(false);
    }
  }

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (!busy && companyId) setDragging(true);
  }

  function onDragLeave() {
    setDragging(false);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    if (!companyId || busy) return;
    const file = e.dataTransfer.files[0];
    if (file) void handleFile(file);
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  }

  const dropDisabled = !companyId || busy;

  return (
    <div style={overlay} onClick={onClose} role="dialog" aria-modal="true" aria-label={t("orders.import.title")}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={panelHeader}>
          <h2 style={titleStyle}>{t("orders.import.title")}</h2>
          <button type="button" className="btn sm" onClick={onClose} aria-label={t("action.cancel")}>
            <Ic.x />
          </button>
        </div>

        {/* Wizard position: this modal IS step 1 (upload). */}
        <div style={{ marginBottom: 18 }}>
          <ImportStepper current={1} completed={0} />
        </div>

        {/* Company selector */}
        <label style={labelStyle} htmlFor="import-company">
          {t("orders.import.selectCompany")}
        </label>
        <div style={{ marginBottom: 16 }}>
          <CompanyCombobox
            companies={companies}
            value={companyId || null}
            onChange={(id) => setCompanyId(id ?? "")}
            disabled={busy}
          />
        </div>

        {/* Drop zone */}
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => !dropDisabled && fileInputRef.current?.click()}
          role="button"
          tabIndex={dropDisabled ? -1 : 0}
          onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !dropDisabled) fileInputRef.current?.click(); }}
          aria-disabled={dropDisabled}
          style={{
            ...dropZone,
            ...(dragging ? dropZoneActive : {}),
            ...(dropDisabled ? dropZoneDisabled : {}),
          }}
        >
          <span style={{ opacity: 0.55, marginBottom: 8 }}><Ic.upload /></span>
          <span style={{ fontSize: 13.5, color: dropDisabled ? "var(--ink-4)" : "var(--ink-2)", textAlign: "center" }}>
            {busy ? t("orders.import.processing") : t("orders.import.dropHint")}
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.xlsx,.xls,.csv,.docx,.eml,.txt,.msg"
            style={{ display: "none" }}
            onChange={onInputChange}
            disabled={dropDisabled}
          />
        </div>

        {error && (
          <div style={errorBox}>{error}</div>
        )}

        <div style={actions}>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t("action.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "var(--overlay)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: 16,
};
const panel: CSSProperties = {
  background: "var(--surface)",
  borderRadius: 12,
  padding: 24,
  width: 520,
  maxWidth: "100%",
  boxShadow: "var(--shadow-2)",
};
const panelHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 20,
};
const titleStyle: CSSProperties = { margin: 0, fontSize: 17, color: "var(--brand-ink)" };
const labelStyle: CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "var(--ink-2)",
  marginBottom: 6,
};
const dropZone: CSSProperties = {
  border: "2px dashed var(--border)",
  borderRadius: 10,
  padding: "32px 20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  cursor: "pointer",
  transition: "border-color .15s ease, background .15s ease",
  background: "var(--surface-2)",
  minHeight: 120,
  justifyContent: "center",
};
const dropZoneActive: CSSProperties = {
  borderColor: "var(--brand-primary)",
  background: "var(--brand-primary-50)",
};
const dropZoneDisabled: CSSProperties = {
  cursor: "not-allowed",
  opacity: 0.6,
};
const errorBox: CSSProperties = {
  marginTop: 12,
  padding: "10px 14px",
  background: "var(--err-bg)",
  border: "1px solid var(--err)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--err)",
};
const actions: CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: 20,
};
